const UNSAFE = /[^a-z0-9]/g;

const randomEntropy = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
};

/** Hidden username for Clerk instances that require one while the UI stays email-only. */
export const clerkUsernameFromEmail = (
  email: string,
  entropy: string = randomEntropy()
): string => {
  const local = email.split("@")[0] ?? "user";
  const base = local.toLowerCase().replace(UNSAFE, "").slice(0, 16) || "user";
  const suffix =
    entropy.toLowerCase().replace(UNSAFE, "").slice(0, 12) || "user";
  return `${base}_${suffix}`.slice(0, 32);
};

export interface InvitationTicket {
  readonly status: "complete" | "sign_in" | "sign_up" | null;
  readonly token: string | null;
}

export const parseInvitationTicket = (search: string): InvitationTicket => {
  const params = new URLSearchParams(search);
  const token = params.get("__clerk_ticket");
  const status = params.get("__clerk_status");
  if (status === "sign_in" || status === "sign_up" || status === "complete") {
    return { status, token };
  }
  return { status: null, token };
};

const inflight = new Map<string, Promise<void>>();

/** One shared attempt across React strict-mode remounts. A second call waits. */
export const shareInflight = (
  key: string,
  run: () => Promise<void>
): Promise<void> => {
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }
  const promise = Promise.resolve().then(run);
  inflight.set(key, promise);
  return promise;
};

/** Email Clerk put on an IdP-initiated or invitation ticket, if the payload parses. */
export const emailAddressFromClerkTicket = (token: string): string | null => {
  const segment = token.split(".")[1];
  if (!segment) {
    return null;
  }
  try {
    const base64 = segment.replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const payload = JSON.parse(atob(padded)) as {
      su?: { email_address?: unknown };
    };
    const email = payload.su?.email_address;
    return typeof email === "string" && email.includes("@") ? email : null;
  } catch {
    return null;
  }
};

let heldTicket: InvitationTicket | null = null;

/**
 * Read an IdP-initiated Clerk ticket once, then drop it from the address bar.
 * A later call in the same page load returns the same ticket until release.
 */
export const takeClerkTicketFromLocation = (): InvitationTicket => {
  if (heldTicket?.token) {
    return heldTicket;
  }
  if (typeof window === "undefined") {
    return { status: null, token: null };
  }
  const parsed = parseInvitationTicket(window.location.search);
  if (!(parsed.token && parsed.status)) {
    return { status: null, token: null };
  }
  heldTicket = parsed;
  const url = new URL(window.location.href);
  url.searchParams.delete("__clerk_ticket");
  url.searchParams.delete("__clerk_status");
  window.history.replaceState(
    null,
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
  return parsed;
};

export const releaseClerkTicket = (): void => {
  heldTicket = null;
};
